import React, { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { messagesApi, notificationApi, customersApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { fmtDate } from '@/lib/format';
import { MessagesSquare, Send } from 'lucide-react';
import { EmptyState } from '@/components/empty/EmptyState';
import { toast } from 'sonner';

export default function InboxPage() {
  const [threads, setThreads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');

  const load = async () => {
    const [ts, cs, ob] = await Promise.all([messagesApi.threads(), customersApi.list(), notificationApi.outbox()]);
    setThreads(ts); setCustomers(cs); setOutbox(ob);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedId) return;
    messagesApi.list({ customer_id: selectedId }).then(setMessages);
    messagesApi.markRead(selectedId).then(() => {
      setThreads(prev => prev.map(t => t.customer?.id === selectedId ? { ...t, unread_count: 0 } : t));
    });
  }, [selectedId]);

  const sendMsg = async () => {
    if (!text.trim() || !selectedId) return;
    try {
      await messagesApi.create({ customer_id: selectedId, channel: 'note', direction: 'outbound', body: text });
      setText('');
      const ms = await messagesApi.list({ customer_id: selectedId });
      setMessages(ms);
      toast.success('Message saved');
    } catch { toast.error('Failed'); }
  };

  return (
    <AppShell title="Inbox">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-semibold">Inbox</h1>
        <p className="text-sm text-muted-foreground">Unified customer conversations and notification history.</p>
      </div>
      <Tabs defaultValue="threads">
        <TabsList>
          <TabsTrigger value="threads">Conversations ({threads.length})</TabsTrigger>
          <TabsTrigger value="outbox">Notifications outbox ({outbox.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="threads" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[400px]">
            <Card className="lg:col-span-4">
              <CardHeader><CardTitle className="text-sm">Threads</CardTitle></CardHeader>
              <CardContent className="p-0">
                {threads.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No conversations yet.</div> : (
                  <div className="divide-y">
                    {threads.map(t => (
                      <button key={t.customer?.id} onClick={() => setSelectedId(t.customer?.id)} className={`block w-full text-left px-3 py-3 hover:bg-muted/40 ${selectedId === t.customer?.id ? 'bg-muted' : ''}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-sm">{t.customer?.name || 'Unknown'}</div>
                          {t.unread_count > 0 && <Badge className="shrink-0">{t.unread_count}</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{t.last_message?.body}</div>
                        <div className="text-[10px] text-muted-foreground">{fmtDate(t.last_message?.created_at)} · {t.count} msg</div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="lg:col-span-8">
              <CardContent className="p-4 flex flex-col h-[60vh] lg:h-[500px]">
                {!selectedId ? (
                  <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Select a thread to view messages.</div>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto space-y-2">
                      {messages.map(m => (
                        <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.direction === 'outbound' ? 'ml-auto bg-primary/10' : 'bg-muted'}`}>
                          <div className="break-words">{m.body}</div>
                          <div className="text-[10px] text-muted-foreground mt-1">{m.channel} • {fmtDate(m.created_at)}</div>
                        </div>
                      ))}
                      {messages.length === 0 && <div className="text-sm text-muted-foreground">No messages yet.</div>}
                    </div>
                    <div className="pt-3 border-t flex gap-2">
                      <Textarea value={text} onChange={e => setText(e.target.value)} placeholder="Type an internal note or message…" className="flex-1" rows={2} />
                      <Button onClick={sendMsg}><Send className="h-4 w-4" /></Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="outbox" className="mt-4">
          {outbox.length === 0 ? (
            <EmptyState icon={MessagesSquare} title="No notifications sent yet" description="When jobs change state, notifications appear here (mocked delivery)." />
          ) : (
            <Card><CardContent className="p-0"><div className="divide-y">
              {outbox.map(o => (
                <div key={o.id} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm">{o.trigger}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{o.channel} • {o.status}</div>
                  </div>
                  {o.subject && <div className="text-xs mt-1"><span className="text-muted-foreground">Subject:</span> {o.subject}</div>}
                  <div className="text-xs">{o.body}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">To: {o.to || '—'} • {fmtDate(o.created_at)}</div>
                </div>
              ))}
            </div></CardContent></Card>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
